import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Mail, MessageSquare } from "lucide-react";

type MethodKey = "text" | "email" | "call";
type VerifyStatus = "idle" | "success" | "error" | "verified";

export interface NovalaVerifyModalProps {
  phone?: string;
  email?: string;
  correctCode?: string;
  onClose?: () => void;
  onVerified?: () => void;
  onCodeSubmit?: ((code: string) => Promise<boolean>) | null;
  onResend?: (() => void) | null;
  defaultMethod?: MethodKey;
  availableMethods?: MethodKey[];
}

function maskEmail(e: string): string {
  if (!e || !e.includes("@")) return e || "";
  const [name, domain] = e.split("@");
  if (name.length <= 1) return name + "***@" + domain;
  return name[0] + "***@" + domain;
}

function maskPhone(p: string): string {
  if (!p) return "";
  const digits = String(p).replace(/[^\d+]/g, "");
  if (digits.startsWith("+1") && digits.length === 12) {
    return "+1 " + digits.slice(2, 5) + " XXX XX" + digits.slice(-2);
  }
  return digits;
}

export default function NovalaVerifyModal({
  phone = "",
  email = "",
  correctCode = "123456",
  onClose = () => {},
  onVerified = () => {},
  onCodeSubmit = null,
  onResend = null,
  defaultMethod = "email",
  availableMethods = ["email", "text"]
}: NovalaVerifyModalProps) {
  const { t } = useTranslation();
  const initialMethod: MethodKey = (availableMethods.includes(defaultMethod) ? defaultMethod : availableMethods[0]) as MethodKey;
  const [method, setMethod] = useState<MethodKey>(initialMethod);
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [status, setStatus] = useState<VerifyStatus>("idle");
  const [smsUnavailableNote, setSmsUnavailableNote] = useState<string>("");
  const [resendCooldown, setResendCooldown] = useState<number>(30);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const destination = useMemo(() => {
    if (method === "email") return maskEmail(email);
    return maskPhone(phone);
  }, [method, phone, email]);

  const codeStr = code.join("");
  const isFull = codeStr.length === 6;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (!isFull) {
      setStatus(prev => (prev === "success" || prev === "error") ? "idle" : prev);
      return;
    }
    let cancelled = false;
    (async () => {
      const submitFn = onCodeSubmit;
      if (submitFn) {
        try {
          const ok = await submitFn(codeStr);
          if (!cancelled) setStatus(prev => prev === "verified" ? prev : (ok ? "success" : "error"));
        } catch (e) {
          if (!cancelled) setStatus(prev => prev === "verified" ? prev : "error");
        }
      } else {
        const ok = codeStr === String(correctCode);
        if (!cancelled) setStatus(prev => prev === "verified" ? prev : (ok ? "success" : "error"));
      }
    })();
    return () => { cancelled = true; };
  }, [codeStr, isFull]);

  const resetCode = () => {
    setCode(["", "", "", "", "", ""]);
    setStatus("idle");
    setTimeout(() => inputRefs.current[0]?.focus(), 0);
  };

  const handleChange = (i: number, v: string) => {
    const ch = v.replace(/\D/g, "").slice(-1);
    if (!ch) return;
    const next = code.slice();
    next[i] = ch;
    setCode(next);
    inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (code[i]) {
        const next = code.slice(); next[i] = ""; setCode(next);
      } else if (i > 0) {
        inputRefs.current[i - 1]?.focus();
        const next = code.slice(); next[i - 1] = ""; setCode(next);
      }
    }
    if (e.key === "ArrowLeft" && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let j = 0; j < txt.length; j++) next[j] = txt[j];
    setCode(next);
    inputRefs.current[Math.min(txt.length, 5)]?.focus();
  };

  const handleSwitchMethod = (key: MethodKey) => {
    if (key === method) return;
    if (key === "text") {
      setSmsUnavailableNote(t("checkout.verify.smsUnavailable"));
      return;
    }
    setSmsUnavailableNote("");
    setMethod(key);
    resetCode();
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;
    resetCode();
    setResendCooldown(30);
    if (onResend) onResend();
  };

  const handleContinue = () => {
    if (status !== "success") return;
    setStatus("verified");
    setTimeout(() => onVerified(), 800);
  };

  const activeIcon = method === "email"
    ? <Mail size={28} strokeWidth={1.8} className="text-ink" />
    : <MessageSquare size={28} strokeWidth={1.8} className="text-ink" />;

  const getBoxClass = (i: number): string => {
    const base = "w-10 rounded-sm bg-white text-center font-mono outline-none transition-colors border-hairline border";
    if (status === "success" || status === "verified") return base + " border-brand text-brand";
    if (status === "error") return base + " text-danger";
    if (code[i]) return base + " border-ink text-ink";
    return base + " border-stroke text-ink";
  };

  const renderCard = (key: MethodKey, label: string, dest: string, icon: React.ReactNode) => {
    const active = method === key;
    return (
      <button
        type="button"
        onClick={() => handleSwitchMethod(key)}
        className={"rounded-md cursor-pointer p-3 text-left w-full font-sans text-ink bg-white " + (active ? "border border-brand" : "border border-hairline")}
      >
        <div className="mb-1 flex items-center gap-2">
          {icon}
          <span className="text-small font-medium">{label}</span>
          <span className={"ml-auto inline-flex h-3.5 w-3.5 items-center justify-center rounded-full " + (active ? "border border-ink" : "border border-hairline")}>
            {active && <span className="block h-1.5 w-1.5 rounded-full bg-ink" />}
          </span>
        </div>
        <div className="font-mono text-tiny text-muted">{dest}</div>
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center p-4 font-sans"
      style={{ background: "rgba(14,26,26,0.35)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full rounded-md border border-hairline bg-white"
        style={{ maxWidth: 480, padding: "28px 24px" }}
        onClick={(e) => e.stopPropagation()}
      >

        <div className="mb-3 flex justify-center">
          {activeIcon}
        </div>

        <div className="mb-1.5 text-center text-lg font-medium text-ink" style={{ fontSize: 18 }}>
          {t("checkout.verify.title")}
        </div>
        <div className="mb-0.5 text-center text-small text-ink leading-normal">
          {t("checkout.verify.codeSentTo")}
        </div>
        <div className="mb-5 text-center font-mono text-small font-medium text-ink">
          {destination}
        </div>

        <div className="mb-2 text-small font-medium text-ink">
          {t("checkout.verify.sendCodeBy")}
        </div>
        <div className={"grid grid-cols-2 gap-2 " + (smsUnavailableNote ? "mb-2" : "mb-5")}>
          {renderCard("email", t("checkout.verify.methodEmail"), maskEmail(email), <Mail size={16} className="text-ink" />)}
          {renderCard("text", t("checkout.verify.methodText"), phone ? maskPhone(phone) : t("checkout.verify.noPhoneOnFile"), <MessageSquare size={16} className="text-ink" />)}
        </div>
        {smsUnavailableNote && (
          <div className="mb-5 text-tiny text-muted leading-snug">{smsUnavailableNote}</div>
        )}

        <div className="mb-2 text-small font-medium text-ink">
          {t("checkout.verify.verificationCode")}
        </div>
        <div className="mb-1.5 flex justify-start gap-2">
          {code.map((c, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              inputMode="numeric"
              maxLength={1}
              value={c}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              disabled={status === "verified"}
              className={getBoxClass(i)}
              style={{ height: 44, fontSize: 16, fontWeight: 500 }}
            />
          ))}
        </div>
        <div className="mb-4 text-tiny text-muted">
          {status === "error" ? t("checkout.verify.invalidCode") : t("checkout.verify.expiresIn", { time: "9:42" })}
        </div>

        <button
          type="button"
          onClick={handleContinue}
          disabled={status !== "success"}
          className={"w-full rounded-md border-none p-3 text-small font-medium text-white font-sans " + (status === "success" ? "cursor-pointer bg-brand" : "cursor-not-allowed bg-disabledText")}
        >
          {status === "verified" ? t("checkout.verify.verified") : t("checkout.verify.continue")}
        </button>

        <div className="mt-3.5 border-t border-hairline pt-3 text-tiny text-ink">
          {t("checkout.verify.didntReceive")} {" "}
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0}
            className={"border-none bg-transparent p-0 text-tiny font-medium font-sans underline " + (resendCooldown > 0 ? "cursor-not-allowed text-muted" : "cursor-pointer text-brand")}
          >
            {resendCooldown > 0 ? t("checkout.verify.resendIn", { seconds: resendCooldown }) : t("checkout.verify.resend")}
          </button>
        </div>
        <div className="mt-2 text-tiny text-muted leading-normal">
          {t("checkout.verify.securityDisclaimer")}
        </div>
      </div>
    </div>
  );
}
